/** One ability's live state for the skill bar. */
export interface SkillHudItem {
  id: string;
  key: string;
  name: string;
  /** Icon stem under public/assets/skills/. */
  icon: string;
  /** Hero accent (hex) — tints the skill glyph. */
  accent: string;
  /** Seconds left on cooldown (0 = ready). */
  remaining: number;
  /** Total cooldown (for the sweep fraction). */
  cooldown: number;
  /** True while this ability's timed buff is active (glows). */
  active?: boolean;
}

interface SkillCell {
  root: HTMLElement;
  overlay: HTMLElement;
  text: HTMLElement;
}

/** The player health bar's live state (null hides the bar). */
export interface HealthHudState {
  health: number;
  max: number;
}

/** The duel kill/death score (null hides it). */
export interface ScoreHudState {
  kills: number;
  deaths: number;
}

/** The passive resource meter's live state (null hides the meter). */
export interface ResourceHudState {
  /** Resource name (e.g. 'Tempo'), shown as the meter label. */
  name: string;
  /** Fill fraction 0..1. */
  fraction: number;
  /** Hero accent (hex) — tints the fill. */
  accent: string;
  /** True briefly after a surge — flashes the meter. */
  surging: boolean;
}

/**
 * Minimal in-run HUD: a large speed readout (the one number that matters in a
 * surf game), a small meta line (slow-mo status + fps), a center banner for run
 * results, and a bottom-center skill bar (MMO/LoL style) for hero abilities.
 */
export class Hud {
  private frames = 0;
  private fpsWindowStart = performance.now();
  private fps = 0;
  private readonly speedNum: HTMLElement;
  private readonly metaEl: HTMLElement;
  private readonly banner: HTMLElement;
  private readonly heroEl: HTMLElement;
  private readonly skillbar: HTMLElement;
  private readonly resourceEl: HTMLElement;
  private readonly resourceLabel: HTMLElement;
  private readonly resourceFill: HTMLElement;
  private readonly healthEl: HTMLElement;
  private readonly healthFill: HTMLElement;
  private readonly healthText: HTMLElement;
  private readonly scoreEl: HTMLElement;
  private readonly skillCells = new Map<string, SkillCell>();
  /** Signature of the currently-rendered skill set, to know when to rebuild. */
  private skillSig = '';

  constructor(
    root: HTMLElement,
    /** Touch mode (spec 0029): tap handlers make skill cells act as buttons. */
    private readonly skillTaps?: {
      onPress: (key: string) => void;
      onRelease: (key: string) => void;
    },
  ) {
    this.heroEl = document.createElement('div');
    this.heroEl.className = 'hud-hero';
    root.append(this.heroEl);

    const speed = document.createElement('div');
    speed.className = 'hud-speed';
    this.speedNum = document.createElement('span');
    this.speedNum.className = 'hud-speed-num';
    const unit = document.createElement('span');
    unit.className = 'hud-speed-unit';
    unit.textContent = 'u/s';
    speed.append(this.speedNum, unit);

    this.metaEl = document.createElement('div');
    this.metaEl.className = 'hud-meta';
    root.append(speed, this.metaEl);

    this.banner = document.createElement('div');
    this.banner.id = 'run-banner';
    this.banner.style.display = 'none';
    document.body.appendChild(this.banner);

    // Bottom-center skill bar, on its own (not in the bottom-left HUD column).
    this.skillbar = document.createElement('div');
    this.skillbar.id = 'skillbar';
    this.skillbar.style.display = 'none';
    document.body.appendChild(this.skillbar);

    // Passive resource meter, a slim bar sitting just above the skill bar.
    this.resourceEl = document.createElement('div');
    this.resourceEl.id = 'resource-meter';
    this.resourceEl.style.display = 'none';
    this.resourceLabel = document.createElement('span');
    this.resourceLabel.className = 'resource-label';
    const track = document.createElement('div');
    track.className = 'resource-track';
    this.resourceFill = document.createElement('div');
    this.resourceFill.className = 'resource-fill';
    track.appendChild(this.resourceFill);
    this.resourceEl.append(this.resourceLabel, track);
    document.body.appendChild(this.resourceEl);

    // Player health bar (spec 0026) — above the resource meter, LoL-style.
    this.healthEl = document.createElement('div');
    this.healthEl.id = 'health-bar';
    this.healthEl.style.display = 'none';
    const healthTrack = document.createElement('div');
    healthTrack.className = 'health-track';
    this.healthFill = document.createElement('div');
    this.healthFill.className = 'health-fill';
    healthTrack.appendChild(this.healthFill);
    this.healthText = document.createElement('span');
    this.healthText.className = 'health-text';
    this.healthEl.append(healthTrack, this.healthText);
    document.body.appendChild(this.healthEl);

    // Duel score (spec 0028) — top-right kills/deaths.
    this.scoreEl = document.createElement('div');
    this.scoreEl.id = 'duel-score';
    this.scoreEl.style.display = 'none';
    document.body.appendChild(this.scoreEl);
  }

  /** Call once per rendered frame. `timerLine`/`statusLine` are optional meta. */
  update(speedUnitsPerSec: number, timerLine = '', statusLine = ''): void {
    this.frames += 1;
    const now = performance.now();
    if (now - this.fpsWindowStart >= 500) {
      this.fps = Math.round((this.frames * 1000) / (now - this.fpsWindowStart));
      this.frames = 0;
      this.fpsWindowStart = now;
    }
    this.speedNum.textContent = `${Math.round(speedUnitsPerSec)}`;
    this.metaEl.textContent = [timerLine, statusLine, `${this.fps} fps`]
      .filter((l) => l !== '')
      .join('   ');
  }

  /** Set the small selected-hero label (top of the HUD), tinted to the accent. */
  setHero(name: string, accent: string): void {
    this.heroEl.textContent = name;
    this.heroEl.style.color = accent;
  }

  /**
   * Drive the bottom-center skill bar. Pass `[]` to hide it (feel-only heroes).
   * Cells are built when the ability set changes, then updated every frame:
   * a cooling skill dims and shows a clockwise dark sweep + a seconds.decimal
   * countdown; a ready skill reads at full brightness.
   */
  setAbilities(items: SkillHudItem[]): void {
    if (items.length === 0) {
      this.skillbar.style.display = 'none';
      this.skillSig = '';
      return;
    }

    const sig = items.map((i) => `${i.id}:${i.key}:${i.icon}`).join('|');
    if (sig !== this.skillSig) {
      this.rebuildSkillCells(items);
      this.skillSig = sig;
    }
    this.skillbar.style.display = 'flex';

    for (const it of items) {
      const cell = this.skillCells.get(it.id);
      if (!cell) {
        continue;
      }
      const cooling = it.remaining > 0.05;
      cell.root.classList.toggle('cooling', cooling);
      cell.root.classList.toggle('active', it.active === true);
      if (cooling) {
        const frac = it.cooldown > 0 ? Math.min(1, Math.max(0, it.remaining / it.cooldown)) : 0;
        const deg = frac * 360;
        cell.overlay.style.background = `conic-gradient(rgba(8,11,15,0.78) ${deg}deg, rgba(8,11,15,0) ${deg}deg)`;
        cell.text.textContent = it.remaining.toFixed(1);
      } else {
        cell.overlay.style.background = 'transparent';
        cell.text.textContent = '';
      }
    }
  }

  /** Drive the passive resource meter. Pass `null` to hide it (no-passive heroes). */
  setResource(state: ResourceHudState | null): void {
    if (!state) {
      this.resourceEl.style.display = 'none';
      return;
    }
    this.resourceEl.style.display = 'flex';
    this.resourceEl.style.setProperty('--accent', state.accent);
    this.resourceEl.classList.toggle('surging', state.surging);
    this.resourceLabel.textContent = state.name;
    // A surge reads as the bar slamming full, then draining into the new charge.
    const pct = state.surging ? 100 : Math.min(100, Math.max(0, state.fraction * 100));
    this.resourceFill.style.width = `${pct.toFixed(1)}%`;
  }

  /** Drive the player health bar. Pass `null` to hide it (practice maps). */
  setHealth(state: HealthHudState | null): void {
    if (!state) {
      this.healthEl.style.display = 'none';
      return;
    }
    this.healthEl.style.display = 'flex';
    const pct = state.max > 0 ? Math.min(100, Math.max(0, (state.health / state.max) * 100)) : 0;
    this.healthFill.style.width = `${pct.toFixed(1)}%`;
    this.healthText.textContent = `${Math.round(state.health)} / ${state.max}`;
  }

  /** Drive the duel score. Pass `null` to hide it (maps without a duelist). */
  setScore(state: ScoreHudState | null): void {
    if (!state) {
      this.scoreEl.style.display = 'none';
      return;
    }
    this.scoreEl.style.display = 'block';
    this.scoreEl.textContent = `K ${state.kills}  ·  D ${state.deaths}`;
  }

  private rebuildSkillCells(items: SkillHudItem[]): void {
    this.skillbar.textContent = '';
    this.skillCells.clear();
    for (const it of items) {
      const root = document.createElement('div');
      root.className = 'skill';
      root.style.setProperty('--accent', it.accent);
      root.title = it.name;

      const glyph = document.createElement('span');
      glyph.className = 'skill-glyph';
      glyph.style.setProperty('--icon', `url("/assets/skills/${it.icon}.svg")`);

      const overlay = document.createElement('div');
      overlay.className = 'skill-cd';

      const text = document.createElement('span');
      text.className = 'skill-cd-text';

      const key = document.createElement('span');
      key.className = 'skill-key';
      key.textContent = it.key.replace('Key', '');

      root.append(glyph, overlay, text, key);
      if (this.skillTaps) {
        const taps = this.skillTaps;
        root.addEventListener('pointerdown', (e) => {
          e.preventDefault();
          root.setPointerCapture(e.pointerId);
          taps.onPress(it.key);
        });
        const release = () => taps.onRelease(it.key);
        root.addEventListener('pointerup', release);
        root.addEventListener('pointercancel', release);
      }
      this.skillbar.appendChild(root);
      this.skillCells.set(it.id, { root, overlay, text });
    }
  }

  setBanner(text: string | null): void {
    this.banner.style.display = text ? 'block' : 'none';
    this.banner.textContent = text ?? '';
  }
}
